import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SessionService } from '@edl-development/edl-common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NavigationItemId } from 'src/app/modals/edl-modal-details/navigation-item-id.enum';
import { CplCycle } from 'src/app/model/cpl-cycle.model';
import { CplItem } from 'src/app/model/cpl-item.model';
import { CplRequirement } from 'src/app/model/enum/cpl-requirement.enum';
import { ComplianceItemHelpersService } from 'src/app/services/compliance-item-helpers/compliance-item-helpers.service';
import { UserCycleDetailsModal } from 'src/app/supervision-management/user-cycle-details-modal/user-cycle-details-modal.component';
import { IconFulfillmentState } from './icon-fulfillment-state.enum';
import { IconRequirementBadge } from './icon-requirement-badge.enum';

@Component({
    selector: 'compliance-item-evidence',
    templateUrl: './compliance-item-evidence.component.html',
    styleUrls: ['./compliance-item-evidence.component.scss', '../../shared-compliance-items.scss']
})
export class ComplianceItemEvidenceComponent implements OnInit {
    private _complianceItem: CplItem;
    private _canManageItem: boolean = false;

    @Input() cplCycle: CplCycle;
    @Input() isUser: boolean = false;
    @Input() 
    get complianceItem() { 
        return this._complianceItem;
    }

    set complianceItem(value: CplItem) {
        this._complianceItem = value;
        this._canManageItem = this.complianceItemHelpersService.canManageItem(this._complianceItem);
    };

    @Output() onRefreshList = new EventEmitter<void>();

    get canManageItem() {
        return this._canManageItem;
    }

    constructor(
        private ngbModal: NgbModal,
        private complianceItemHelpersService: ComplianceItemHelpersService,
        private sessionService: SessionService
    ) { }

    canShowNotes: boolean = false;
    NavigationItemId = NavigationItemId;

    get CanViewDetails() {
        return this.complianceItemHelpersService.canViewDetails(this.complianceItem, this.isUser);
    }

    ngOnInit(): void {
        this.canShowNotes = this.generateCanShowNotes();
    }

    viewDetails(userCycle: CplCycle, navigationItemId: NavigationItemId){
        this.openDetailsModal(userCycle, navigationItemId)
            .then((shouldRefresh: boolean) => {
                if (shouldRefresh) {
                    this.refreshList(); 
                }
            }, () => {
                this.refreshList();
            })
    }

    openDetailsModal(userCycle: CplCycle, navigationItemId: NavigationItemId){
        let modalRef = this.ngbModal.open(UserCycleDetailsModal, {
            size: 'lg',
            backdrop: 'static',
            windowClass: 'edl-modal-details'
        });

        modalRef.componentInstance.originalUserCycle = userCycle;
        modalRef.componentInstance.complianceItem = this.complianceItem;
        modalRef.componentInstance.startingNavigationItemId = navigationItemId;
        modalRef.componentInstance.CanManageItem = this.canManageItem;
        modalRef.componentInstance.isUser = this.isUser;
        return modalRef.result;
    }


    canShowEvidence(userCycle: CplCycle) {
        return (!this.complianceItemHelpersService.isFinalUserStatus(userCycle.UserStatus) && this.complianceItem.EvidenceRequired !== CplRequirement.NONE) || userCycle.HasAttachments;
    }

    getAttachmentsState(userCycle: CplCycle): IconFulfillmentState {
        return userCycle.HasAttachments ? IconFulfillmentState.FULFILLED : IconFulfillmentState.NOT_FULFILLED;
    }

    getQuestionnaireState(userCycle: CplCycle): IconFulfillmentState {
        return userCycle.HasCompletedQuestionnaire ? IconFulfillmentState.FULFILLED : IconFulfillmentState.NOT_FULFILLED;
    }

    getNotesState(userCycle: CplCycle): IconFulfillmentState {
        return userCycle.HasNotes ? IconFulfillmentState.FULFILLED : IconFulfillmentState.NOT_FULFILLED;
    }

    // Mirrors UserCycleDetailsModal's showEvidence (user-cycle-details-modal.component.ts): evidence
    // already on file must stay reachable even if the item's requirement later changed to NONE.
    isEvidenceApplicable(complianceItem: CplItem, userCycle: CplCycle): boolean {
        return complianceItem.EvidenceRequired !== CplRequirement.NONE || userCycle.HasAttachments;
    }

    isQuestionnaireApplicable(cplCycle: CplCycle): boolean {
        return !!cplCycle.QfmID;
    }

    getAttachmentsBadge(complianceItem: CplItem, userCycle: CplCycle): IconRequirementBadge {
        if (complianceItem.EvidenceRequired !== CplRequirement.REQUIRED) {
            return IconRequirementBadge.NONE;
        }
        return userCycle.HasAttachments ? IconRequirementBadge.REQUIRED_FULFILLED : IconRequirementBadge.REQUIRED_UNFULFILLED;
    }

    // Mirrors UserCycleDetailsModal's "required" indicator (user-cycle-details-modal.component.ts),
    // which keys off complianceItem.QfmRequired, not just whether a questionnaire is assigned —
    // an assigned-but-not-required questionnaire must not badge as "required" here either.
    getQuestionnaireBadge(complianceItem: CplItem, userCycle: CplCycle): IconRequirementBadge {
        if (!userCycle.QfmID || !complianceItem.QfmRequired) {
            return IconRequirementBadge.NONE;
        }
        return userCycle.HasCompletedQuestionnaire ? IconRequirementBadge.REQUIRED_FULFILLED : IconRequirementBadge.REQUIRED_UNFULFILLED;
    }

    private static readonly REQUIRED_BADGE_PHRASE: Partial<Record<IconRequirementBadge, string>> = {
        [IconRequirementBadge.REQUIRED_UNFULFILLED]: 'required, not yet fulfilled',
        [IconRequirementBadge.REQUIRED_FULFILLED]: 'required, fulfilled'
    };

    private formatRequirableTooltip(label: string, badge: IconRequirementBadge, fulfilled: boolean): string {
        const phrase = ComplianceItemEvidenceComponent.REQUIRED_BADGE_PHRASE[badge]
            ?? (fulfilled ? 'optional, fulfilled' : 'optional, not yet fulfilled');
        return `${label} — ${phrase}`;
    }

    getAttachmentsTooltip(userCycle: CplCycle): string {
        return this.formatRequirableTooltip('Evidence', this.getAttachmentsBadge(this.complianceItem, userCycle), userCycle.HasAttachments);
    }

    getQuestionnaireTooltip(userCycle: CplCycle): string {
        // Unassigned: icon is hidden entirely, so this text is never seen, but it must not claim
        // a non-existent "optional" state. Assigned-but-not-required falls through to
        // formatRequirableTooltip's "optional" phrasing, per complianceItem.QfmRequired.
        if (!userCycle.QfmID) {
            return 'Questionnaire — not assigned';
        }
        return this.formatRequirableTooltip('Questionnaire', this.getQuestionnaireBadge(this.complianceItem, userCycle), userCycle.HasCompletedQuestionnaire);
    }

    getNotesTooltip(userCycle: CplCycle): string {
        return `Notes — ${userCycle.HasNotes ? 'available' : 'not added'}`;
    }

    private generateCanShowNotes() {
        return this.complianceItemHelpersService.isSupervisorView(this.isUser);
    }

    private refreshList() {
        this.onRefreshList.emit();
    }
}
